import React from 'react';
import { InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CashBreakdown } from 'types/fms';
import LOCALS from 'commons/locals';
import type { CashDenomination } from '../cash-settlement-currency';
import CashBreakdownPanel from './CashBreakdownPanel';
import VarianceStatus from './VarianceStatus';

export type CashCountFormValues = {
  beginningCash: number;
  treasuryCashInAmount: number;
  treasuryCashOutAmount: number;
  actualCash: number;
  note: string;
  cashCounter: string;
  cashBreakdown: CashBreakdown;
};

type CashCountFormProps = {
  values: CashCountFormValues;
  currencySymbol: string;
  amountPrecision: number;
  submitting: boolean;
  showCashBreakdown: boolean;
  bills: CashDenomination[];
  coins: CashDenomination[];
  saleCashTotal: number;
  recycledConsignmentCashTotal: number;
  dailyCashChangeAmount: number;
  cashRefundTotal: number;
  systemCashSales: number;
  treasuryCashMovementNet: number;
  expectedCash: number;
  variance: number;
  cashBreakdownTotal: number;
  submitDisabled: boolean;
  formatAmount: (amount: number | null | undefined) => string;
  formatInputAmount: (value: string | number | undefined) => string;
  parseInputAmount: (value: string | undefined) => number;
  onTreasuryCashChange: (
    field: 'treasuryCashInAmount' | 'treasuryCashOutAmount',
    value: number | null,
  ) => void;
  onActualCashChange: (value: number | null) => void;
  onNoteChange: (value: string) => void;
  onCashBreakdownChange: (
    type: 'bills' | 'coins',
    denomination: string,
    count: number | null,
  ) => void;
  onClearCashBreakdown: () => void;
  onToggleCashBreakdown: () => void;
  onSubmit: () => void;
};

const CashCountForm: React.FC<CashCountFormProps> = ({
  values,
  currencySymbol,
  amountPrecision,
  submitting,
  showCashBreakdown,
  bills,
  coins,
  saleCashTotal,
  recycledConsignmentCashTotal,
  dailyCashChangeAmount,
  cashRefundTotal,
  systemCashSales,
  treasuryCashMovementNet,
  expectedCash,
  variance,
  cashBreakdownTotal,
  submitDisabled,
  formatAmount,
  formatInputAmount,
  parseInputAmount,
  onTreasuryCashChange,
  onActualCashChange,
  onNoteChange,
  onCashBreakdownChange,
  onClearCashBreakdown,
  onToggleCashBreakdown,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-6 pb-4 border-b">
        {t(LOCALS.daily_register_close_cash_count)}
      </h2>

      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 gap-3 sm:gap-0">
          <label className="text-sm font-medium text-gray-700 sm:min-w-[140px]">
            {t(LOCALS.daily_register_close_opening_cash)}
          </label>
          <div className="flex-1 sm:ml-6">
            <div className="flex items-center">
              <span className="text-gray-500 mr-3 text-lg font-medium">
                {currencySymbol}
              </span>
              <div className="bg-gray-50 px-4 py-3 rounded-md flex-1 h-12 flex items-center">
                <span className="text-gray-900 font-medium text-lg">
                  {formatAmount(values.beginningCash)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between py-2 gap-3 sm:gap-0">
          <label className="text-sm font-medium text-gray-700 sm:min-w-[140px] sm:pt-3">
            {t(LOCALS.daily_register_close_till_cash_movement)}
          </label>
          <div className="flex-1 sm:ml-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-3 min-w-[48px]">
                  {t(LOCALS.daily_register_close_cash_in_from_safe)}
                </span>
                <span className="text-gray-500 mr-3 text-lg font-medium">
                  {currencySymbol}
                </span>
                <InputNumber
                  value={values.treasuryCashInAmount}
                  onChange={(value) =>
                    onTreasuryCashChange('treasuryCashInAmount', value)
                  }
                  className="flex-1 !h-12"
                  min={0}
                  precision={amountPrecision}
                  disabled={submitting}
                  formatter={formatInputAmount}
                  parser={parseInputAmount}
                  controls={false}
                />
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-3 min-w-[48px]">
                  {t(LOCALS.daily_register_close_cash_out_to_safe)}
                </span>
                <span className="text-gray-500 mr-3 text-lg font-medium">
                  {currencySymbol}
                </span>
                <InputNumber
                  value={values.treasuryCashOutAmount}
                  onChange={(value) =>
                    onTreasuryCashChange('treasuryCashOutAmount', value)
                  }
                  className="flex-1 !h-12"
                  min={0}
                  precision={amountPrecision}
                  disabled={submitting}
                  formatter={formatInputAmount}
                  parser={parseInputAmount}
                  controls={false}
                />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {t(LOCALS.daily_register_close_cash_movement_net)}:{' '}
              {currencySymbol} {formatAmount(treasuryCashMovementNet)}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 gap-3 sm:gap-0">
          <label className="text-sm font-medium text-gray-700 sm:min-w-[140px]">
            {t(LOCALS.daily_register_close_system_cash_net)}
          </label>
          <div className="flex-1 sm:ml-6">
            <div className="flex items-center">
              <span className="text-gray-500 mr-3 text-lg font-medium">
                {currencySymbol}
              </span>
              <div className="bg-gray-50 px-4 py-3 rounded-md flex-1 h-12 flex items-center">
                <span className="text-gray-900 font-medium text-lg">
                  {formatAmount(systemCashSales)}
                </span>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs text-gray-500">
              <span>
                {t(LOCALS.daily_register_close_sales_cash_in)}: {currencySymbol}{' '}
                {formatAmount(saleCashTotal)}
              </span>
              <span>
                {t(
                  LOCALS.daily_register_close_recycle_consignment_cash_payout_deduction,
                )}
                : {currencySymbol} {formatAmount(recycledConsignmentCashTotal)}
              </span>
              <span>
                {t(LOCALS.daily_register_close_cash_change_deduction)}:{' '}
                {currencySymbol} {formatAmount(dailyCashChangeAmount)}
              </span>
              <span>
                {t(LOCALS.daily_register_close_cash_refund_deduction)}:{' '}
                {currencySymbol} {formatAmount(cashRefundTotal)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 gap-3 sm:gap-0">
          <label className="text-sm font-medium text-gray-700 sm:min-w-[140px]">
            {t(LOCALS.daily_register_close_expected_cash)}
          </label>
          <div className="flex-1 sm:ml-6">
            <div className="flex items-center">
              <span className="text-blue-600 mr-3 text-lg font-medium">
                {currencySymbol}
              </span>
              <div className="bg-blue-50 px-4 py-3 rounded-md flex-1 h-12 flex items-center border border-blue-100">
                <span className="text-blue-700 font-medium text-lg">
                  {formatAmount(expectedCash)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 gap-3 sm:gap-0">
          <label className="text-sm font-medium text-gray-700 sm:min-w-[140px]">
            {t(LOCALS.daily_register_close_counted_cash)}{' '}
            <span className="text-red-500">*</span>
          </label>
          <div className="flex-1 sm:ml-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex items-center flex-1">
                <span className="text-gray-500 mr-3 text-lg font-medium">
                  {currencySymbol}
                </span>
                <InputNumber
                  value={values.actualCash}
                  onChange={onActualCashChange}
                  className="flex-1 !h-12"
                  min={0}
                  precision={amountPrecision}
                  disabled={submitting || showCashBreakdown}
                  formatter={formatInputAmount}
                  parser={parseInputAmount}
                  controls={false}
                />
              </div>
              <button
                onClick={onToggleCashBreakdown}
                className={`px-5 py-3 rounded-lg font-medium text-sm transition-all duration-200 h-12 whitespace-nowrap ${
                  showCashBreakdown
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
                disabled={submitting}
              >
                {t(LOCALS.daily_register_close_denomination_breakdown)}{' '}
                {showCashBreakdown ? '▲' : '▼'}
              </button>
            </div>
          </div>
        </div>

        {showCashBreakdown && (
          <CashBreakdownPanel
            cashBreakdown={values.cashBreakdown}
            bills={bills}
            coins={coins}
            disabled={submitting}
            currencySymbol={currencySymbol}
            totalAmount={cashBreakdownTotal}
            formatAmount={formatAmount}
            onChange={onCashBreakdownChange}
            onClear={onClearCashBreakdown}
          />
        )}

        <VarianceStatus
          variance={variance}
          actualCash={values.actualCash}
          currencySymbol={currencySymbol}
          formatAmount={formatAmount}
        />

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between py-2 gap-3 sm:gap-0">
          <label className="text-sm font-medium text-gray-700 sm:min-w-[140px] sm:pt-3">
            {t(LOCALS.daily_register_close_over_short_reason)}
          </label>
          <div className="flex-1 sm:ml-6">
            <textarea
              value={values.note}
              onChange={(e) => onNoteChange(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base resize-none"
              rows={4}
              disabled={submitting}
              placeholder={
                t(LOCALS.daily_register_close_over_short_placeholder) || ''
              }
            />
          </div>
        </div>

        <div className="pt-8 border-t">
          <button
            onClick={onSubmit}
            disabled={submitDisabled}
            className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-4 rounded-lg font-semibold transition-all duration-200 text-lg h-14 shadow-lg hover:shadow-xl"
          >
            {submitting
              ? t(LOCALS.submitting)
              : t(LOCALS.daily_register_close_complete_action)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashCountForm;
